import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Shield, Search, ArrowUpDown, CheckCircle, XCircle, RefreshCw } from "lucide-react";
import { safeLog } from "@/utils/safeLogger";

interface UserWithRoles {
  id: string;
  email: string;
  created_at: string;
  is_admin: boolean;
  roles: string[];
}

const PROTECTED_ADMIN_EMAIL = 'justus.wiedemann@celsios.io';

export const UserRolesManager = () => {
  const [users, setUsers] = useState<UserWithRoles[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<keyof UserWithRoles>("created_at");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [authError, setAuthError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const { toast } = useToast();

  const loadUsers = async (isRetry = false) => {
    try {
      setIsLoading(true);
      setAuthError(null);
      safeLog.info('[UserRolesManager] Loading users', { isRetry, retryCount });

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setAuthError("Not authenticated");
        return;
      }

      const { data, error } = await supabase.functions.invoke('get-all-users-with-roles', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        safeLog.error('[UserRolesManager] Error loading users', error as Error);
        setAuthError(error.message);
        throw error;
      }

      safeLog.info('[UserRolesManager] Users loaded successfully', { count: data.users?.length || 0 });
      setUsers(data.users || []);
      setRetryCount(0); // Reset retry count on success
    } catch (error) {
      safeLog.error('[UserRolesManager] Failed to load users', error as Error);
      
      // Auto-retry mechanism (max 3 retries)
      if (!isRetry && retryCount < 3) {
        safeLog.info('[UserRolesManager] Retrying load', { attempt: retryCount + 1 });
        setRetryCount(prev => prev + 1);
        setTimeout(() => loadUsers(true), 1000 * (retryCount + 1)); // Exponential backoff
        return;
      }
      
      toast({
        title: "Fehler beim Laden",
        description: "Benutzer konnten nicht geladen werden. Bitte versuche es erneut.",
        variant: "destructive",
        action: (
          <Button variant="outline" size="sm" onClick={() => loadUsers()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Erneut versuchen
          </Button>
        ),
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    safeLog.info('[UserRolesManager] Component mounted, loading users');
    loadUsers();

    // Subscribe to realtime changes in user_roles table
    const channel = supabase
      .channel('user_roles_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_roles'
        },
        (payload) => {
          safeLog.info('[UserRolesManager] Realtime role change detected', { 
            event: payload.eventType,
            hasNewData: !!payload.new,
            hasOldData: !!payload.old
          });
          // Delay reload slightly to ensure DB has committed
          setTimeout(() => loadUsers(), 500);
        }
      )
      .subscribe();

    return () => {
      safeLog.info('[UserRolesManager] Component unmounting');
      supabase.removeChannel(channel);
    };
  }, []);

  const toggleAdminRole = async (userId: string, currentValue: boolean) => {
    const targetUser = users.find(u => u.id === userId);
    if (!targetUser) return;

    // Prevent toggling the protected admin account
    if (targetUser.email === PROTECTED_ADMIN_EMAIL && currentValue) {
      toast({
        title: "Geschützter Account",
        description: "Dieser Account ist geschützt und kann nicht entfernt werden.",
        variant: "destructive",
      });
      return;
    }

    const action = currentValue ? 'remove_admin' : 'add_admin';
    const previousState = currentValue;
    
    safeLog.info('[UserRolesManager] Toggling admin role', { 
      userEmail: targetUser.email, 
      action,
      currentState: currentValue 
    });

    // Optimistic update
    setUsers(prev => prev.map(u => 
      u.id === userId 
        ? { ...u, is_admin: !currentValue }
        : u
    ));

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('manage-user-role', {
        body: { target_user_id: userId, action },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        safeLog.error('[UserRolesManager] Edge function error', error as Error);
        throw error;
      }

      if (!data?.success) {
        safeLog.error('[UserRolesManager] Edge function returned failure', new Error(data?.message));
        throw new Error(data?.message || 'Failed to update role');
      }

      safeLog.info('[UserRolesManager] Role toggle successful', { 
        roleExists: data.role_exists,
        expectedState: !previousState 
      });

      // Verify persistence by checking DB directly
      const { data: verifyData, error: verifyError } = await supabase
        .from('user_roles')
        .select('id')
        .eq('user_id', userId)
        .eq('role', 'admin')
        .maybeSingle();

      if (verifyError) {
        safeLog.error('[UserRolesManager] Verification query failed', verifyError);
      }

      const roleExists = !!verifyData;
      const expectedState = action === 'add_admin';

      if (roleExists !== expectedState) {
        safeLog.error('[UserRolesManager] Persistence verification failed', 
          new Error(`Expected: ${expectedState}, Got: ${roleExists}`)
        );
        throw new Error('Role change was not persisted correctly');
      }

      safeLog.info('[UserRolesManager] Persistence verified', { roleExists, expectedState });

      toast({
        title: "Erfolg",
        description: `${targetUser.email} ${action === 'add_admin' ? 'ist jetzt Admin' : 'ist kein Admin mehr'}`,
      });

      // Reload users with delay to ensure DB has committed
      setTimeout(() => loadUsers(), 500);
      
    } catch (error) {
      safeLog.error('[UserRolesManager] Failed to toggle admin role', error as Error);
      
      // Revert optimistic update on error
      setUsers(prev => prev.map(u => 
        u.id === userId 
          ? { ...u, is_admin: previousState }
          : u
      ));

      toast({
        title: "Fehler",
        description: error instanceof Error ? error.message : "Rolle konnte nicht geändert werden",
        variant: "destructive",
        action: (
          <Button variant="outline" size="sm" onClick={() => toggleAdminRole(userId, previousState)}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Erneut versuchen
          </Button>
        ),
      });
    }
  };

  const filteredAndSorted = useMemo(() => {
    let filtered = users.filter(user => 
      user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    filtered.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc' 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      
      if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
        return sortDirection === 'asc'
          ? (aVal === bVal ? 0 : aVal ? 1 : -1)
          : (aVal === bVal ? 0 : bVal ? 1 : -1);
      }

      return 0;
    });

    return filtered;
  }, [users, searchTerm, sortField, sortDirection]);

  const handleSort = (field: keyof UserWithRoles) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }: { field: keyof UserWithRoles }) => {
    if (sortField !== field) return <ArrowUpDown className="h-4 w-4 ml-1 opacity-40" />;
    return <ArrowUpDown className={`h-4 w-4 ml-1 ${sortDirection === 'desc' ? 'rotate-180' : ''}`} />;
  };

  if (authError) {
    return (
      <Card className="p-6">
        <div className="text-center text-destructive">
          <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-semibold mb-2">Access Denied</h3>
          <p className="text-sm text-muted-foreground">{authError}</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          <h2 className="text-2xl font-bold">User Roles Management</h2>
        </div>
        <Badge variant="secondary">{users.length} Users</Badge>
      </div>

      <div className="mb-4 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Loading users...</div>
      ) : filteredAndSorted.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          {searchTerm ? 'No users found matching your search.' : 'No users found.'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b">
              <tr>
                <th 
                  className="text-left py-3 px-4 font-semibold cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('email')}
                >
                  <div className="flex items-center">
                    Email
                    <SortIcon field="email" />
                  </div>
                </th>
                <th 
                  className="text-left py-3 px-4 font-semibold cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('created_at')}
                >
                  <div className="flex items-center">
                    Created At
                    <SortIcon field="created_at" />
                  </div>
                </th>
                <th 
                  className="text-center py-3 px-4 font-semibold cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('is_admin')}
                >
                  <div className="flex items-center justify-center">
                    Admin
                    <SortIcon field="is_admin" />
                  </div>
                </th>
                <th className="text-center py-3 px-4 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSorted.map((user) => {
                const isProtected = user.email === PROTECTED_ADMIN_EMAIL;
                
                return (
                  <tr key={user.id} className="border-b hover:bg-muted/30">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        {user.email}
                        {isProtected && (
                          <Badge variant="outline" className="text-xs">
                            <Shield className="h-3 w-3 mr-1" />
                            Protected
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-muted-foreground">
                      {new Date(user.created_at).toLocaleDateString('de-DE', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {user.is_admin ? (
                        <CheckCircle className="h-5 w-5 text-green-600 mx-auto" />
                      ) : (
                        <XCircle className="h-5 w-5 text-muted-foreground mx-auto" />
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Switch
                          checked={user.is_admin}
                          onCheckedChange={() => toggleAdminRole(user.id, user.is_admin)}
                          disabled={isProtected && user.is_admin}
                        />
                        <span className="text-sm text-muted-foreground">
                          {user.is_admin ? 'Admin' : 'User'}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};